import { FC } from 'react';
import { User, Mail, Phone } from 'lucide-react';
import { cn } from '../../../../utils/cn';
import type { TherapistProfile } from '../../../../types/therapist-dashboard.types';

export interface ProfileHeaderProps {
  profile: TherapistProfile;
}

export const ProfileHeader: FC<ProfileHeaderProps> = ({ profile }) => {
  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-6">
      <div className="flex flex-col sm:flex-row items-center gap-6">
        <div className="relative">
          {profile.avatar ? (
            <img
              src={profile.avatar}
              alt={`${profile.firstName} ${profile.lastName}`}
              className="w-24 h-24 rounded-full object-cover border-4 border-white dark:border-slate-700 shadow-lg"
            />
          ) : (
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center border-4 border-white dark:border-slate-700 shadow-lg">
              <User className="w-10 h-10 text-white" />
            </div>
          )}
          <span
            className={cn(
              'absolute bottom-1 right-1 w-5 h-5 rounded-full border-2 border-white dark:border-slate-700',
              profile.status === 'active' && 'bg-green-500',
              profile.status === 'inactive' && 'bg-gray-400',
              profile.status === 'on_leave' && 'bg-amber-500'
            )}
            title={`Status: ${profile.status.replace('_', ' ')}`}
          />
        </div>
        <div className="text-center sm:text-left flex-1">
          <h3 className="text-xl font-bold text-gray-900 dark:text-slate-100">
            {profile.firstName} {profile.lastName}
          </h3>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mt-2 text-sm text-gray-600 dark:text-slate-400">
            <span className="flex items-center gap-1 justify-center sm:justify-start">
              <Mail className="w-4 h-4" />
              {profile.email}
            </span>
            {profile.phone && (
              <span className="flex items-center gap-1 justify-center sm:justify-start">
                <Phone className="w-4 h-4" />
                {profile.phone}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-2 justify-center sm:justify-start">
            <span
              className={cn(
                'px-2 py-0.5 rounded-full text-xs font-medium',
                profile.status === 'active' &&
                  'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
                profile.status === 'inactive' &&
                  'bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-slate-300',
                profile.status === 'on_leave' &&
                  'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
              )}
            >
              {profile.status.replace('_', ' ')}
            </span>
            <span className="text-xs text-gray-500 dark:text-slate-500">
              Member since{' '}
              {new Date(profile.memberSince).toLocaleDateString('en-US', {
                month: 'short',
                year: 'numeric',
              })}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
